import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, TextField,
  Select, MenuItem, Switch, Button, IconButton, Divider, Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import blinkscribeapi from "../api/blinkscribeapi";

/* Per-provider AI note instructions (Phase 1, 2026-09-01).
   "Always" instructions apply to every note the provider records;
   "condition" instructions fire only when a trigger keyword appears in the
   visit transcript. Injected server-side at note generation — scoped to the
   logged-in provider, other providers unaffected. */

const EMPTY = { id: null, name: "", kind: "always", trigger_keywords: "", instruction_text: "", enabled: true, sort: 0, visit_type: "any" };

export default function NoteInstructionsModal({ open, onClose, initialName }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await blinkscribeapi.get("/v1/note_instructions/list");
      const data = res?.data ?? res;
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setItems(list);
      return list;
    } catch (e) {
      setMsg("Could not load instructions");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSel(EMPTY); setMsg("");
    load().then((list) => {
      // Opened from an "AI instructions applied" chip on a note — preselect it.
      if (initialName) {
        const hit = list.find((it) => it.name === initialName);
        if (hit) setSel({ ...hit });
      }
    });
  }, [open, initialName]);

  const save = async (row) => {
    if (!row.name?.trim() || !row.instruction_text?.trim()) {
      setMsg("Name and instruction text are required");
      return;
    }
    setSaving(true); setMsg("");
    try {
      await blinkscribeapi.post("/v1/note_instructions/save", row);
      setMsg("Saved");
      await load();
      if (!row.id) setSel(EMPTY);
    } catch (e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => save({ ...row, enabled: !row.enabled });

  const remove = async () => {
    if (!sel.id) return;
    if (!window.confirm(`Delete "${sel.name}"? Notes will stop applying it immediately.`)) return;
    setSaving(true);
    try {
      await blinkscribeapi.post("/v1/note_instructions/delete", { id: sel.id });
      setSel(EMPTY);
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
        Note Instructions
        <Typography sx={{ ml: 1.5, fontSize: 13, color: "#6B7280", flex: 1 }}>
          Your personal AI commands and condition templates, applied to notes you record
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", gap: 2, minHeight: 480 }}>
        {/* list */}
        <Box sx={{ width: 320, borderRight: "1px solid #E5E7EB", pr: 2, overflowY: "auto" }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button size="small" variant="outlined" fullWidth sx={{ textTransform: "none" }}
              onClick={() => { setSel(EMPTY); setMsg(""); }}>
              + New
            </Button>
            <Button size="small" variant="outlined" fullWidth component="label" sx={{ textTransform: "none" }}>
              Import .docx
              <input type="file" hidden accept=".docx"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  setMsg("Importing…");
                  try {
                    const fd = new FormData();
                    fd.append("file", f);
                    const res = await blinkscribeapi.post("/v1/note_instructions/extract_doc", fd);
                    const d = res?.data ?? res;
                    if (d?.drafts?.length) {
                      await load();
                      setMsg(d.message || `Imported ${d.drafts.length} draft(s) — review, then enable`);
                    } else {
                      setMsg(d?.message || "Import failed");
                    }
                  } catch {
                    setMsg("Import failed");
                  }
                }} />
            </Button>
          </Box>
          {loading ? <CircularProgress size={22} sx={{ m: 2 }} /> : items.map((it) => (
            <Box key={it.id}
              onClick={() => { setSel({ ...it }); setMsg(""); }}
              sx={{
                p: 1, mb: 0.5, borderRadius: 1.5, cursor: "pointer",
                border: sel.id === it.id ? "1.5px solid #E17954" : "1px solid #E5E7EB",
                opacity: it.enabled ? 1 : 0.55,
                display: "flex", alignItems: "center", gap: 1,
              }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.name}
                </Typography>
                <Chip size="small"
                  label={(it.kind === "condition" ? "condition-triggered" : "every note")
                    + (it.visit_type === "new" ? " · new consults" : it.visit_type === "followup" ? " · follow-ups" : "")}
                  sx={{ height: 18, fontSize: 10, mt: 0.3, bgcolor: it.kind === "condition" ? "#EFF6FF" : "#FDF6F3", color: it.kind === "condition" ? "#1D4ED8" : "#E17954" }} />
              </Box>
              <Switch size="small" checked={!!it.enabled}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(it)} />
            </Box>
          ))}
          {!loading && items.length === 0 && (
            <Typography sx={{ fontSize: 13, color: "#6B7280", p: 1 }}>
              No instructions yet — create one, or paste in a template.
            </Typography>
          )}
        </Box>
        {/* editor */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField size="small" label="Name" fullWidth value={sel.name}
              onChange={(e) => setSel({ ...sel, name: e.target.value })} />
            <Select size="small" value={sel.kind} sx={{ width: 200 }}
              onChange={(e) => setSel({ ...sel, kind: e.target.value })}>
              <MenuItem value="always">Apply to every note</MenuItem>
              <MenuItem value="condition">Condition-triggered</MenuItem>
            </Select>
            <Select size="small" value={sel.visit_type || "any"} sx={{ width: 180 }}
              onChange={(e) => setSel({ ...sel, visit_type: e.target.value })}>
              <MenuItem value="any">Any visit type</MenuItem>
              <MenuItem value="new">New consults only</MenuItem>
              <MenuItem value="followup">Follow-ups only</MenuItem>
            </Select>
          </Box>
          {sel.kind === "condition" && (
            <TextField size="small" label="Trigger keywords" fullWidth value={sel.trigger_keywords}
              helperText="Comma-separated. Applied when any keyword appears in the visit transcript — e.g. dyslipidemia, statin, cholesterol"
              onChange={(e) => setSel({ ...sel, trigger_keywords: e.target.value })} />
          )}
          <TextField label="Instruction / template text" multiline minRows={14} maxRows={22} fullWidth
            value={sel.instruction_text}
            placeholder="Paste or write the instruction the AI should follow for your notes…"
            onChange={(e) => setSel({ ...sel, instruction_text: e.target.value })}
            sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }} />
          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: 13 }}>Enabled</Typography>
            <Switch checked={!!sel.enabled} onChange={(e) => setSel({ ...sel, enabled: e.target.checked })} />
            <Typography sx={{ fontSize: 12.5, color: msg === "Saved" ? "#059669" : "#DC2626", flex: 1 }}>{msg}</Typography>
            {sel.id && (
              <Button color="error" size="small" sx={{ textTransform: "none" }} onClick={remove} disabled={saving}>
                Delete
              </Button>
            )}
            <Button variant="contained" size="small" disabled={saving}
              sx={{ textTransform: "none", bgcolor: "#E17954", "&:hover": { bgcolor: "#c9613e" } }}
              onClick={() => save(sel)}>
              {saving ? "Saving…" : sel.id ? "Save changes" : "Create"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
